import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Tabs,
  Clickable,
  FormField,
  Feedback,
} from '@fashionnovateam/nova-ui-toolkit';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { AddTagFormData } from '../types';
import { useGetMetaDatas } from '../../../../Lib/Hooks/MetadataService';

export function ProductClassificationSection() {
  const { control, watch, setValue, formState } =
    useFormContext<AddTagFormData>();

  const { data: metaDatas } = useGetMetaDatas(['ns_master_merchHierarchy']);

  const divisions = useMemo(() => {
    if (metaDatas?.ns_master_merchHierarchy) {
      return metaDatas?.ns_master_merchHierarchy.divisions;
    }
    return null;
  }, [metaDatas]);

  const currentDivision = watch('division');
  const currentDepartment = watch('department');
  const currentCategory = watch('category');
  const currentClass = watch('class');

  const divisionsOptions = useMemo(() => {
    if (!divisions) return [];

    const allowedDivisions = [
      { label: 'Womens', value: "WOMEN'S" },
      { label: 'Mens', value: "MEN'S" },
      { label: 'Kids', value: "KID'S" },
      { label: 'Nova Sport', value: 'Nova Sport' },
    ];

    return allowedDivisions.filter((division) => divisions[division.value]);
  }, [divisions]);

  const departmentOptions = useMemo(() => {
    if (!divisions || !currentDivision) return [];
    const departements = divisions[currentDivision];
    if (!departements) return [];

    return Object.keys(departements).map((department) => ({
      label: department,
      value: department,
    }));
  }, [divisions, currentDivision]);

  const categoryOptions = useMemo(() => {
    if (!divisions || !currentDivision || !currentDepartment) return [];

    const departements = divisions[currentDivision];
    if (!departements || !departements[currentDepartment]) return [];

    const categories = departements[currentDepartment];
    return Object.keys(categories).map((category) => ({
      label: category,
      value: category,
    }));
  }, [divisions, currentDivision, currentDepartment]);

  const classOptions = useMemo(() => {
    if (
      !divisions ||
      !currentDivision ||
      !currentDepartment ||
      !currentCategory
    )
      return [];

    const departements = divisions[currentDivision];
    if (!departements || !departements[currentDepartment]) return [];

    const categories = departements[currentDepartment];
    if (!categories || !categories[currentCategory]) return [];

    const classes = categories[currentCategory];
    if (Array.isArray(classes)) {
      return classes.map((className) => ({
        label: className,
        value: className,
      }));
    }

    return Object.keys(classes).map((className) => ({
      label: className,
      value: className,
    }));
  }, [divisions, currentDivision, currentDepartment, currentCategory]);

  useEffect(() => {
    if (currentDivision && departmentOptions.length > 0) {
      const isValidDepartment = departmentOptions.some(
        (opt) => opt.value === currentDepartment,
      );
      if (!isValidDepartment) {
        setValue('department', '');
        setValue('category', '');
        setValue('class', '');
      }
    }
  }, [currentDivision, departmentOptions, currentDepartment, setValue]);

  useEffect(() => {
    if (currentDepartment && categoryOptions.length > 0) {
      const isValidCategory = categoryOptions.some(
        (opt) => opt.value === currentCategory,
      );
      if (!isValidCategory) {
        setValue('category', '');
        setValue('class', '');
      }
    }
  }, [currentDepartment, categoryOptions, currentCategory, setValue]);

  useEffect(() => {
    if (currentCategory && classOptions.length > 0) {
      const isValidClass = classOptions.some(
        (opt) => opt.value === currentClass,
      );
      if (!isValidClass) {
        setValue('class', '');
      }
    }
  }, [currentCategory, classOptions, currentClass, setValue]);

  return (
    <div className="rounded-xl border border-neutral-200 shadow-sm p-5 space-y-4">
      <h3 className="text-lg font-medium text-gray-900">
        Product Classification
        <span className="text-danger-700 ml-1">*</span>
      </h3>

      <FormField label="Division" required>
        {divisionsOptions.length > 0 ? (
          <Tabs.Group type="segment" data-testid="division-tabs">
            {divisionsOptions.map((division) => (
              <Clickable
                key={division.value}
                data-testid={`${division.value.toLowerCase().replace(/\s+/g, '-')}-division-tab-button`}
                onClick={() => setValue('division', division.value)}
              >
                <Tabs.Item
                  isActive={currentDivision === division.value}
                  type="segment"
                  data-testid={`${division.value.toLowerCase().replace(/\s+/g, '-')}-division-tab`}
                >
                  {division.label}
                </Tabs.Item>
              </Clickable>
            ))}
          </Tabs.Group>
        ) : (
          <Feedback
            type="error"
            title="Division options are not available. Please try again later."
            className="mt-2"
          />
        )}
      </FormField>

      <ControlledFormInput
        name="department"
        label="Department"
        control={control}
        inputType="select"
        required
        inputProps={{
          placeholder: 'Select..',
          className: 'w-full',
          options: departmentOptions,
          disabled: !currentDivision || departmentOptions.length === 0,
        }}
      />

      <ControlledFormInput
        name="category"
        label="Category"
        control={control}
        inputType="select"
        required
        inputProps={{
          placeholder: 'Select..',
          className: 'w-full',
          options: categoryOptions,
          disabled: !currentDepartment || categoryOptions.length === 0,
        }}
      />

      <ControlledFormInput
        name="class"
        label="Class"
        control={control}
        inputType="select"
        required
        inputProps={{
          placeholder: 'Select..',
          className: 'w-full',
          options: classOptions,
          disabled: !currentCategory || classOptions.length === 0,
        }}
      />

      {formState.isSubmitted &&
        (!currentDivision ||
          !currentDepartment ||
          !currentCategory ||
          !currentClass) && (
          <Feedback
            type="error"
            title="Please complete the Division, Department, Category and Class for this product."
            className="my-6"
          />
        )}
    </div>
  );
}
